/**
 * TypeSeverityApproversEditor — renders the (type × severity) approval
 * threshold matrix with inline-edit cells. Replaces the v1 free-form
 * category-approvers editor.
 *
 * Backing routes:
 *   GET /api/v2/rule-type-approvers
 *   PUT /api/v2/rule-type-approvers/{type}/{severity}
 */

import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { ApiError } from "~/lib/api";
import {
    RULE_SEVERITIES,
    RULE_TYPES,
    type RuleSeverity,
    type RuleType,
    type TypeSeverityApprover,
    useSetTypeSeverityApprover,
    useTypeSeverityApprovers,
} from "~/lib/rules";

function findCell(
    rows: TypeSeverityApprover[],
    type: RuleType,
    severity: RuleSeverity,
): number {
    return rows.find((r) => r.type === type && r.severity === severity)?.approversCount ?? 1;
}

export function TypeSeverityApproversEditor(): React.ReactElement {
    const q = useTypeSeverityApprovers();
    const rows = q.data ?? [];

    if (q.isLoading) {
        return (
            <div className="stack-xs" aria-label="Loading approvers matrix">
                <Loader2 size={14} className="animate-spin" />
            </div>
        );
    }

    return (
        <div className="card-surface" style={{ padding: "var(--space-4)" }}>
            <table className="data-table" style={{ width: "100%" }}>
                <thead>
                    <tr>
                        <th style={{ textAlign: "left" }}>Type</th>
                        {RULE_SEVERITIES.map((s) => (
                            <th key={s.value} style={{ textAlign: "center" }}>
                                <span className={`status-pill ${s.pillClass}`}>{s.value}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {RULE_TYPES.map((t) => (
                        <tr key={t.value}>
                            <td>
                                <span className={`chip ${t.chipClass}`}>{t.label}</span>
                            </td>
                            {RULE_SEVERITIES.map((s) => (
                                <td key={s.value} style={{ textAlign: "center" }}>
                                    <ApproverCell
                                        type={t.value}
                                        severity={s.value}
                                        value={findCell(rows, t.value, s.value)}
                                    />
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ApproverCell({
    type, severity, value,
}: { type: RuleType; severity: RuleSeverity; value: number }): React.ReactElement {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<number>(value);
    const mut = useSetTypeSeverityApprover();

    async function save(): Promise<void> {
        try {
            await mut.mutateAsync({ type, severity, approversCount: draft });
            toast.success(`${type}/${severity} → ${draft}`);
            setEditing(false);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : "Save failed");
        }
    }

    if (!editing) {
        return (
            <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                    setDraft(value);
                    setEditing(true);
                }}
                title={`Set approver count for ${type}/${severity}`}
            >
                {value}
            </button>
        );
    }
    return (
        <div className="cluster-xs" style={{ justifyContent: "center" }}>
            <input
                type="number"
                min={1}
                max={5}
                className="input"
                value={draft}
                onChange={(e) => setDraft(Number(e.target.value) || 1)}
                style={{ width: 56, textAlign: "center" }}
                autoFocus
            />
            <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={mut.isPending}>
                {mut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(false)}>
                <X size={12} />
            </button>
        </div>
    );
}
